import KwentaSDK from '@kwenta/sdk'
import { Leaderboard } from '@kwenta/sdk/types'
import { truncateAddress } from '@kwenta/sdk/utils'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { parseEther } from 'ethers/lib/utils.js'

import { notifyError } from 'components/ErrorNotifier'
import { selectProviderSupportsCrossMargin } from 'state/futures/snxPerpsV3/selectors'
import { ThunkConfig } from 'state/types'
import { selectWallet } from 'state/wallet/selectors'
import logError from 'utils/logError'

import { selectValidSearchTerm } from './selectors'

export const fetchV2Leaderboard = createAsyncThunk<
	Awaited<ReturnType<KwentaSDK['stats']['getV2Leaderboard']>>,
	void,
	ThunkConfig
>('stats/fetchV2Leaderboard', async (_, { getState, extra: { sdk } }) => {
	const searchTerm = selectValidSearchTerm(getState())
	const wallet = selectWallet(getState())
	try {
		return await sdk.stats.getV2Leaderboard(searchTerm || '', wallet || '')
	} catch (error) {
		logError(error)
		notifyError('Failed to fetch leaderboard', error)
		throw error
	}
})

export const fetchV3Leaderboard = createAsyncThunk<
	Awaited<ReturnType<KwentaSDK['stats']['getV3Leaderboard']> | undefined>,
	void,
	ThunkConfig
>('stats/fetchV3Leaderboard', async (_, { getState, extra: { sdk } }) => {
	const searchTerm = selectValidSearchTerm(getState())
	const wallet = selectWallet(getState())
	try {
		return await sdk.stats.getV3Leaderboard(searchTerm || '', wallet || '')
	} catch (error) {
		logError(error)
		notifyError('Failed to fetch leaderboard', error)
		throw error
	}
})

export const fetchCompetitionLeaderboard = createAsyncThunk<
	Awaited<ReturnType<KwentaSDK['stats']['getV2Leaderboard']> | undefined>,
	void,
	ThunkConfig
>('stats/fetchV3Leaderboard', async (_, { getState, extra: { sdk } }) => {
	const supportedNetwork = selectProviderSupportsCrossMargin(getState())

	if (!supportedNetwork) return

	try {
		const data = await sdk.stats.getCompetitionLeaderboard()
		const formatted = data.map((item) => {
			const pnl = String(item.pnl).substring(0, 10)

			return {
				...item,
				accountId: item.accountId,
				accountOwner: item.address,
				pnl: parseEther(pnl).toString(),
				pnlWithFeesPaid: parseEther(pnl).toString(),
				liquidations: '0',
				totalTrades: String(item.totalTrades),
				trader: item.address,
				traderShort: truncateAddress(item.address),
				rank: item.rank,
				rankText: String(item.rank), // TODO: We should be able to combine these
			}
		})
		const leaderboard: Leaderboard = {
			wallet: [],
			search: [],
			all: formatted,
		}
		return leaderboard
	} catch (error) {
		logError(error)
		notifyError('Failed to fetch leaderboard', error)
		throw error
	}
})
